import { useMutation, useQuery } from "@apollo/client";
import { parseError } from "apollo";
import Button from "components/base/Button";
import Errors from "components/base/Errors";
import Page from "components/base/Page";
import Spinner, { InlineSpinner } from "components/base/Spinner";
import { Alert } from "components/base/Toast";
import ObjectTagsView from "components/shared/tags/ObjectTagsView";
import moment from "moment";
import { createContext } from "react";
import { Link, useParams } from "react-router-dom";
import CustomerBasicInfoView from "./CustomerBasicInfoView";
import CustomerNotesView from "./CustomerNotesView";
import CustomerPortalView from "./CustomerPortalView";
import DocumentsView from "./DocumentsView";
import {
  ARCHIVE_CUSTOMER,
  SALES_FETCH_CUSTOMER,
  SALES_SAVE_CUSTOMER,
} from "./graphql";
import OrderPlanningView from "./OrderPlanningView";
import PriceListView from "./PriceListView";
import ShippingZoneView from "./ShippingZoneView";

export const CustomerContext = createContext();

const CustomerPage = () => {
  const { id } = useParams();
  const { loading, error, data } = useQuery(SALES_FETCH_CUSTOMER, {
    variables: { id },
  });
  const [archiveCustomer, archiveCustomerRes] = useMutation(ARCHIVE_CUSTOMER, {
    onCompleted() {
      Alert("success", "Customer archived successfully.");
    },
    onError(error) {
      Alert("error", parseError(error));
    },
    refetchQueries: ["SALES_FETCH_CUSTOMERS"],
  });

  const [save, saveRes] = useMutation(SALES_SAVE_CUSTOMER, {
    onCompleted() {
      Alert("success", "Customer updated successfully.");
    },
    onError(error) {
      Alert("error", parseError(error));
    },
  });

  if (loading) return <Spinner />;
  if (error) return <Errors error={error} />;

  const customer = data.customer;

  function archiveHandler() {
    if (window.confirm("Are you sure you want to archive this customer?")) {
      archiveCustomer({ variables: { id: customer.id } });
    }
  }

  return (
    <CustomerContext.Provider value={{ customer, save, saveRes }}>
      <Page
        title={customer.name}
        backTo="./.."
        rightButtons={
          <div className="flex items-center space-x-6">
            {saveRes.loading && (
              <div className="relative">
                <InlineSpinner size={18} />
              </div>
            )}

            <Link
              to={`/sales/reports?partnerId=${customer.odooPartnerId}`}
              target="_blank"
              rel="noreferrer"
            >
              Sales Report
            </Link>

            <a
              href={`${process.env.REACT_APP_SERVER_ADMIN_URL}customer/customer/${customer.id}`}
              target="_blank"
              rel="noreferrer"
            >
              admin
            </a>
          </div>
        }
      >
        <div className="p-6 grid grid-cols-12 gap-6">
          <div className="col-span-8 space-y-6">
            {customer.archivedAt && (
              <div className="card text-red-600 font-bold bg-red-100">
                Customer was archived at{" "}
                {moment(customer.archivedAt).format("MMMM Do YYYY, h:mm a")}.
              </div>
            )}

            <CustomerBasicInfoView />
            <CustomerNotesView />
            <DocumentsView />
          </div>

          <div className="col-span-4 space-y-6">
            <PriceListView />
            <ShippingZoneView />
            <OrderPlanningView />
            <ObjectTagsView
              tags={data.tags}
              contentTypeAppLabel="customer"
              contentTypeName="customer"
              objectId={customer.id}
            />
            <CustomerPortalView />
            {!customer.archivedAt && (
              <div
                className="flex justify-end"
                onClick={archiveHandler}
                loading={archiveCustomerRes.loading}
              >
                <Button danger>Archive Customer</Button>
              </div>
            )}
          </div>
        </div>
      </Page>
    </CustomerContext.Provider>
  );
};

export default CustomerPage;
